import { z } from "zod";

/**
 * How a pipeline resolves the destination record it should write to, instead of
 * always minting a new one (spec §6.1). Stored on `pipelines.identity_policy`;
 * NULL means today's behavior exactly.
 *
 * Identity is source-payload-derived only (spec Decision 1): both strategies read
 * `parsedEvent.payload`, never transform or enrichment output.
 */
const IdentityResolveSchema = z.discriminatedUnion("strategy", [
  z.object({ strategy: z.literal("none") }).strict(),
  z.object({
    strategy: z.literal("event-path"),
    /** Dot-path into the parsed event payload. `[]` wildcards are rejected at
     *  save time — a wildcard could silently pick the first of several values
     *  and update the wrong customer's record (spec §6.3). */
    path: z.string().min(1),
  }).strict(),
  z.object({
    strategy: z.literal("lookup"),
    /** Snowflake (or other lookup-capable) connector instance to query. */
    instanceId: z.string().min(1),
    /** Fully-qualified view/table. Interpolated into SQL — allowlisted at save time. */
    view: z.string().min(1),
    /** Column to match the resolved key against. Interpolated into SQL. */
    keyColumn: z.string().min(1),
    /** Column holding the destination id to read off the matched row. */
    valueColumn: z.string().min(1),
    /** Dot-path into the parsed event payload for the key to match on. */
    keyPath: z.string().min(1),
    /** Coerce the key before binding, for NUMBER key columns. */
    keyType: z.enum(["string", "number"]).optional(),
  }).strict(),
]);

const WriteMaskEntry = z.union([z.literal("all"), z.array(z.string().min(1))]);

export const DestinationIdentityPolicySchema = z.object({
  resolve: IdentityResolveSchema,
  /** Destination property the resolved value matches on. Validated at save time
   *  against the destination connector's declared identity fields. */
  idField: z.string().min(1),
  /**
   * The three moments where the destination record we expected is not there
   * (spec §7). All three are REQUIRED and none defaults to `create`:
   * a pipeline must never introduce a record into a customer's CRM unless the
   * operator asked for it at that specific moment.
   *
   *   create — write a new record
   *   skip   — write nothing; the run finalizes `skipped` with a reason
   *   fail   — write nothing; the run finalizes `failed` and DLQs
   *
   * `skip` is what makes "never create" practical: a customer whose orders
   * mostly have no pre-existing deal can decline to create without DLQ-ing
   * ordinary traffic, which `fail` would.
   */
  onResolutionMiss: z.enum(["create", "skip", "fail"]),
  onResolvedRecordMissing: z.enum(["create", "skip", "fail"]),
  onWriteRace: z.enum(["create", "skip", "fail"]),
  /** Per-operation property allowlist. Absent → "all" (today's behavior). */
  writeMask: z.object({
    create: WriteMaskEntry.optional(),
    adoptedUpdate: WriteMaskEntry.optional(),
    ownedUpdate: WriteMaskEntry.optional(),
  }).strict().optional(),
}).strict();

export type DestinationIdentityPolicy = z.infer<typeof DestinationIdentityPolicySchema>;
export type IdentityResolveSpec = DestinationIdentityPolicy["resolve"];
export type WriteOperation = "create" | "adoptedUpdate" | "ownedUpdate";
